import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import KeyIcon from '@mui/icons-material/VpnKey';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import { REFERRALAPIURL } from '../config';
import { useCompany } from './CompanyContext';
import ReferralInfoSection from './ReferralInfoSection';
import ReferralFormModal from './ReferralFormModal';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  marginTop: '90px',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(6),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.2)',
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  border: '1px solid #e3f2fd',
  marginBottom: theme.spacing(3),
}));

const MethodPaper = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2.5),
  height: '100%',
  borderRadius: theme.spacing(1.5),
  border: `2px solid ${selected ? '#1976d2' : '#e3f2fd'}`,
  backgroundColor: selected ? '#f0f7ff' : '#fff',
  transition: 'all 0.2s ease',
}));

// Numbered section header with completion state
const StepHeader = ({ step, title, done, required }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
    <Avatar
      sx={{
        width: 34,
        height: 34,
        bgcolor: done ? 'success.main' : 'primary.main',
        fontSize: '1rem',
        fontWeight: 700,
      }}
    >
      {done ? <CheckCircleIcon sx={{ fontSize: 22 }} /> : step}
    </Avatar>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>{title}</Typography>
    {required && !done && (
      <Chip label="Required" size="small" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
    )}
  </Stack>
);

// Determine whether the print output should include the shared referrer/patient info.
const resolvePrintFlag = (setting) => {
  if (!setting || setting.print_include_referrer_info == null) return true;
  const val = setting.print_include_referrer_info;
  if (typeof val === 'string') return val !== '0' && val.toLowerCase() !== 'false';
  return !!val;
};

const emptyInfo = {
  initiatingCompanyName: '',
  clientName: '',
  address: '',
};

const ReferralPortal = () => {
  const { guid } = useParams();
  const navigate = useNavigate();
  const { setCompanyBranding, resetCompanyBranding } = useCompany();

  const [referralInfo, setReferralInfo] = useState(emptyInfo);
  const [patientAddressObj, setPatientAddressObj] = useState(null);

  // Recipient selection
  const [recipient, setRecipient] = useState(null); // { mode, companyId, companyName, activeSetting, dynamicFormSchema }
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // Method A: search by name
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Method B: GUID
  const [guidInput, setGuidInput] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [showInfoErrors, setShowInfoErrors] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);

  const onInfoChange = useCallback((name, value) => {
    setReferralInfo((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Fetch and parse a dynamic Form.io schema for the active setting (if configured).
  const fetchDynamicSchema = useCallback(async (activeSetting) => {
    const formId = activeSetting?.referral_form_incoming_id || activeSetting?.referral_form_id;
    if (!formId) return null;
    try {
      const formResp = await axios.get(`${REFERRALAPIURL}/form/${formId}`);
      const rawSchema = formResp.data?.json_schema;
      if (!rawSchema) return null;
      const parsed = typeof rawSchema === 'string' ? JSON.parse(rawSchema) : rawSchema;
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.display) parsed.display = 'form';
      if (!Array.isArray(parsed.components)) parsed.components = [];
      return parsed;
    } catch (e) {
      console.error('Error loading dynamic form schema', e);
      return null;
    }
  }, []);

  // Resolve an internal company by its GUID: verify -> name -> active setting -> dynamic schema.
  const resolveByGuid = useCallback(async (g) => {
    setResolving(true);
    setResolveError(null);
    try {
      const verifyResp = await axios.get(`${REFERRALAPIURL}/referralSettings/guid/verify/${encodeURIComponent(g)}`);
      if (!verifyResp.data?.exists) {
        setResolveError('That referral code was not recognized.');
        setResolving(false);
        return false;
      }
      const companyId = verifyResp.data.company_id;

      let companyName = '';
      try {
        const nameResp = await axios.get(`${REFERRALAPIURL}/referralSettings/company/search`, { params: { q: g } });
        const match = (nameResp.data || []).find((c) => c.company_guid === g);
        if (match) companyName = match.company_name;
      } catch (_) { /* non-fatal */ }

      let activeSetting = null;
      try {
        const settingResp = await axios.get(`${REFERRALAPIURL}/referralSettings/${companyId}/active`);
        activeSetting = settingResp.data;
      } catch (settingErr) {
        if (settingErr?.response?.status !== 404) console.error('Error fetching active setting', settingErr);
      }

      const dynamicFormSchema = await fetchDynamicSchema(activeSetting);

      setCompanyBranding({ name: companyName || undefined, id: companyId });
      setRecipient({ mode: 'company', companyId, companyName, activeSetting, dynamicFormSchema });
      setResolving(false);
      return true;
    } catch (err) {
      console.error('Error resolving company by guid', err);
      setResolveError('Unable to verify that referral code. Please try again.');
      setResolving(false);
      return false;
    }
  }, [fetchDynamicSchema, setCompanyBranding]);

  // Deep-link: /:guid resolves the company and lands on the info step (form stays gated).
  useEffect(() => {
    if (guid) {
      resolveByGuid(guid);
    } else {
      resetCompanyBranding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid]);

  // Method A search
  const handleSearchInput = async (event, value, reason) => {
    if (reason === 'clear') {
      setSearchInput('');
      setSearchOptions([]);
      return;
    }
    if (reason !== 'input') return;
    setSearchInput(value);
    if (value.trim().length < 2) return;
    setSearchLoading(true);
    try {
      const resp = await axios.get(`${REFERRALAPIURL}/referralSettings/company/search`, { params: { q: value.trim() } });
      if (!Array.isArray(resp.data)) {
        console.warn('[ReferralPortal] company search returned non-array payload:', resp.data);
      }
      setSearchOptions(Array.isArray(resp.data) ? resp.data : []);
    } catch (_) {
      setSearchOptions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectCompany = (event, value) => {
    if (!value) return;
    setResolveError(null);
    if (value.source_type === 'internal' && value.company_guid) {
      // Navigate to the shareable deep-link which auto-opens the modal.
      navigate(`/${value.company_guid}`);
      return;
    }
    // External (named, non-FieldWorker) agency: print-only flow.
    setCompanyBranding({ name: value.company_name });
    setRecipient({ mode: 'external', companyId: null, companyName: value.company_name, activeSetting: null, dynamicFormSchema: null });
    setModalOpen(true);
  };

  const handleGuidGo = () => {
    const g = guidInput.trim();
    if (!g) return;
    navigate(`/${g}`);
  };

  const handleStandardForm = () => {
    setResolveError(null);
    resetCompanyBranding();
    setRecipient({ mode: 'standard', companyId: null, companyName: '', activeSetting: null, dynamicFormSchema: null });
    setModalOpen(true);
  };

  const handleSubmitted = (result) => {
    setSubmittedResult(result);
    setModalOpen(false);
  };

  const startOver = () => {
    setSubmittedResult(null);
    setRecipient(null);
    setReferralInfo(emptyInfo);
    setPatientAddressObj(null);
    setShowInfoErrors(false);
    navigate('/');
  };

  const recipientLabel = recipient
    ? recipient.mode === 'standard'
      ? 'Standard FieldWorker Referral Form'
      : recipient.companyName || 'Selected recipient'
    : null;

  const printIncludeReferrerInfo = resolvePrintFlag(recipient?.activeSetting);

  // Section 2 gating — mandatory before the form can be opened.
  const isStandard = recipient?.mode === 'standard';
  const needsClientFields = !!recipient?.dynamicFormSchema;
  const infoRequired = !!recipient && !isStandard;
  const infoErrors = {
    initiatingCompanyName: infoRequired && !(referralInfo.initiatingCompanyName || '').trim(),
    clientName: infoRequired && needsClientFields && !(referralInfo.clientName || '').trim(),
    address: infoRequired && needsClientFields && !(referralInfo.address || '').trim(),
  };
  const missingInfoLabels = [
    infoErrors.initiatingCompanyName && 'Referring Organization',
    infoErrors.clientName && 'Client Name',
    infoErrors.address && 'Client Address',
  ].filter(Boolean);
  const infoComplete = missingInfoLabels.length === 0;
  const canOpenForm = !!recipient && infoComplete;
  const activeStep = !recipient ? 0 : (infoRequired && !infoComplete ? 1 : 2);

  const handleOpenForm = () => {
    if (!canOpenForm) {
      setShowInfoErrors(true);
      return;
    }
    setShowInfoErrors(false);
    setModalOpen(true);
  };

  if (submittedResult?.success) {
    return (
      <StyledContainer maxWidth="md">
        <Card sx={{ borderRadius: 3, textAlign: 'center', p: 5, boxShadow: '0 4px 18px rgba(0,0,0,0.08)' }}>
          <CheckCircleIcon sx={{ fontSize: 70, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Referral Submitted</Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you. Your referral has been received and is pending review.
          </Typography>
          <Button variant="contained" onClick={startOver}>Start a New Referral</Button>
        </Card>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer maxWidth="lg">
      <HeaderCard>
        <CardContent sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" fontWeight="bold">
            Secure Community Referral Portal
          </Typography>
          <Box sx={{ maxWidth: 620, mx: 'auto', mt: 3 }}>
            <Stepper
              activeStep={activeStep}
              sx={{
                '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.85) !important', fontWeight: 600 },
                '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.35)' },
                '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: '#fff' },
                '& .MuiStepIcon-text': { fill: '#1976d2', fontWeight: 700 },
              }}
            >
              <Step completed={!!recipient}><StepLabel>Choose Recipient</StepLabel></Step>
              <Step completed={!!recipient && infoComplete && infoRequired}><StepLabel>Referral Information</StepLabel></Step>
              <Step><StepLabel>Fill &amp; Submit</StepLabel></Step>
            </Stepper>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Section 1: Select Recipient & Form Type */}
      <SectionCard>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <StepHeader step={1} title="Select Recipient & Form Type" done={!!recipient} />

          {resolveError && <Alert severity="error" sx={{ mb: 2 }}>{resolveError}</Alert>}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* A: Search by name */}
            <MethodPaper variant="outlined" selected={recipient?.mode === 'company'}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Chip label="A" color="primary" size="small" sx={{ mr: 1, fontWeight: 700 }} />
                <Typography sx={{ fontWeight: 600 }}>Search by Company Name</Typography>
              </Box>
              <Autocomplete
                options={Array.isArray(searchOptions) ? searchOptions : []}
                loading={searchLoading}
                inputValue={searchInput}
                value={null}
                clearOnBlur={false}
                filterOptions={(x) => x}
                getOptionLabel={(o) => (o ? o.company_name : '')}
                onInputChange={handleSearchInput}
                onChange={handleSelectCompany}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.company_name}-${option.company_guid || option.source_type}`}>
                    <BusinessIcon sx={{ color: '#1976d2', mr: 1, fontSize: 18 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.company_name}</Typography>
                      {option.source_type === 'internal' && (
                        <Typography variant="caption" sx={{ color: '#16a34a' }}>Optimized using FieldWorker</Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Company Name..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ color: '#64748b', mr: 0.5 }} />,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </MethodPaper>

            {/* B: Enter GUID */}
            <MethodPaper variant="outlined">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Chip label="B" color="primary" size="small" sx={{ mr: 1, fontWeight: 700 }} />
                <Typography sx={{ fontWeight: 600 }}>Enter Company GUID</Typography>
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter GUID..."
                value={guidInput}
                onChange={(e) => setGuidInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleGuidGo(); }}
                InputProps={{
                  startAdornment: <KeyIcon sx={{ color: '#64748b', mr: 0.5 }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        disabled={!guidInput.trim() || resolving}
                        onClick={handleGuidGo}
                        edge="end"
                      >
                        {resolving ? <CircularProgress size={16} /> : <SearchIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MethodPaper>

            {/* C: Standard form when no company selected; otherwise show that company's form tile */}
            {recipient?.mode === 'company' ? (
              <MethodPaper variant="outlined" selected>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Chip label="C" color="success" size="small" sx={{ mr: 1, fontWeight: 700 }} />
                  <Typography sx={{ fontWeight: 600 }}>
                    {recipient.dynamicFormSchema
                      ? `${recipient.companyName} — Custom Form`
                      : `${recipient.companyName} — Intake Form`}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenForm}
                  sx={{ mt: 1 }}
                >
                  Open {recipient.companyName} Form
                </Button>
              </MethodPaper>
            ) : (
              <MethodPaper variant="outlined" selected={recipient?.mode === 'standard'}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Chip label="C" color="primary" size="small" sx={{ mr: 1, fontWeight: 700 }} />
                  <Typography sx={{ fontWeight: 600 }}>Use Standard Referral Form</Typography>
                </Box>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DescriptionIcon />}
                  onClick={handleStandardForm}
                  sx={{ mt: 1 }}
                >
                  Use Standard FieldWorker Form
                </Button>
              </MethodPaper>
            )}
          </Stack>
        </CardContent>
      </SectionCard>

      {/* Section 2: Referral Information — mandatory gate before the form can open. */}
      {infoRequired && (
        <SectionCard sx={{ ...(showInfoErrors && !infoComplete && { borderColor: '#f59e0b', borderWidth: 2 }) }}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <StepHeader step={2} title="Complete Referral Information" done={infoComplete} required />
            {showInfoErrors && !infoComplete && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please fill in: {missingInfoLabels.join(', ')}
              </Alert>
            )}
            <ReferralInfoSection
              mode={recipient.mode}
              hasDynamicForm={!!recipient.dynamicFormSchema}
              value={referralInfo}
              onChange={onInfoChange}
              onPatientAddressResolved={setPatientAddressObj}
              showErrors={showInfoErrors}
            />
          </CardContent>
        </SectionCard>
      )}

      {/* Section 3: Open & Finalize — disabled until section 2 is complete. */}
      <SectionCard>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <StepHeader step={infoRequired ? 3 : 2} title="Complete & Finalize" done={false} />
          <Divider sx={{ mb: 3 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<OpenInNewIcon />}
              disabled={!recipient || (infoRequired && !infoComplete && showInfoErrors)}
              onClick={handleOpenForm}
            >
              Open Referral Form
            </Button>
            <Typography variant="body2" color="text.secondary">
              {!recipient
                ? 'Select a recipient or the standard form above to continue.'
                : infoRequired && !infoComplete
                  ? `Complete the required referral information first (${missingInfoLabels.join(', ')}).`
                  : 'Complete the referral form, then submit securely or print for manual submission.'}
            </Typography>
          </Stack>
        </CardContent>
      </SectionCard>

      <ReferralFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        recipient={recipient}
        referralInfo={referralInfo}
        patientAddressObj={patientAddressObj}
        printIncludeReferrerInfo={printIncludeReferrerInfo}
        onSubmitted={handleSubmitted}
      />
    </StyledContainer>
  );
};

export default ReferralPortal;
